import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import userProfileServices from '../services/userProfileServices';
import { ApiState } from '../constants/enums';
import { IUserDetails, IUserPreferences, IUserProfileResponse } from '../types/user.type';

export interface IUserProfileState {
    apiState: {
        getUserProfile: ApiState;
        updateUserProfile: ApiState;
        logout: ApiState;
        invalidatePrallelSessions: ApiState;
    };
    userDetails: IUserDetails;
    userPreferences: IUserPreferences;
    logoutRedirectUrl?: string;
}

const initialState: IUserProfileState = {
    apiState: {
        getUserProfile: ApiState.NOT_STARTED,
        updateUserProfile: ApiState.NOT_STARTED,
        logout: ApiState.NOT_STARTED,
        invalidatePrallelSessions: ApiState.NOT_STARTED,
    },
    userDetails: {
        userName: '',
        emailAddress: '',
        organization: '',
    },
    userPreferences: {
        languageCode: '',
        languageName: '',
        timeZone: '',
        paidById: 0,
        administeredById: 0,
        currencyCode: '',
        defaultRows: 0,
        clientLobFlag: false,
        dateFormat: '',
    },
    logoutRedirectUrl: '',
};

export const getUserProfile = createAsyncThunk<IUserProfileResponse>('settings/get-user-profile', () => {
    return userProfileServices.getUserProfile().then((res) => res);
});

export const logout = createAsyncThunk<any>('settings/logout', () => {
    return userProfileServices.logout().then((res) => res);
});

export const invalidatePrallelSessions = createAsyncThunk<any>('settings/invalidatePrallelSessions', () => {
    return userProfileServices.invalidatePrallelSessions().then((res) => res);
});

export const updateUserProfile = createAsyncThunk<{ data: { message: string } }, IUserPreferences>(
    'settings/update-profile',
    async (data: IUserPreferences) => {
        return userProfileServices.updateUserProfile(data).then((res) => res);
    },
);

const userProfileSlice = createSlice({
    name: 'userProfile',
    initialState,
    extraReducers(builder) {
        builder
            .addCase(getUserProfile.pending, (state) => {
                state.apiState.getUserProfile = ApiState.PENDING;
            })
            .addCase(getUserProfile.fulfilled, (state, action: PayloadAction<IUserProfileResponse>) => {
                state.userDetails = action.payload.data.userDetails;
                state.userPreferences = action.payload.data.userPreferences;
                //state.userPreferences.clientLobFlag = false;
                state.apiState.getUserProfile = ApiState.FULFILLED;
            })
            .addCase(getUserProfile.rejected, (state) => {
                state.apiState.getUserProfile = ApiState.REJECTED;
            })
            .addCase(updateUserProfile.pending, (state) => {
                state.apiState.updateUserProfile = ApiState.PENDING;
            })
            .addCase(updateUserProfile.fulfilled, (state) => {
                state.apiState.updateUserProfile = ApiState.FULFILLED;
            })
            .addCase(updateUserProfile.rejected, (state) => {
                state.apiState.updateUserProfile = ApiState.REJECTED;
            })
            .addCase(logout.pending, (state) => {
                state.apiState.logout = ApiState.PENDING;
            })
            .addCase(logout.fulfilled, (state, action: PayloadAction<any>) => {
                state.logoutRedirectUrl = action.payload.url;
                state.apiState.logout = ApiState.FULFILLED;
            })
            .addCase(logout.rejected, (state) => {
                state.logoutRedirectUrl = '';
                state.apiState.logout = ApiState.REJECTED;
            })

            .addCase(invalidatePrallelSessions.pending, (state) => {
                state.apiState.invalidatePrallelSessions = ApiState.PENDING;
            })
            .addCase(invalidatePrallelSessions.fulfilled, (state) => {
                state.apiState.invalidatePrallelSessions = ApiState.FULFILLED;
            })
            .addCase(invalidatePrallelSessions.rejected, (state) => {
                state.apiState.invalidatePrallelSessions = ApiState.REJECTED;
            });                                
    },
    reducers: {
        changeUserProfileState(state, action: PayloadAction<ApiState>) {
            state.apiState.updateUserProfile = action.payload;
        },
    },
});

export const { changeUserProfileState } = userProfileSlice.actions;
export default userProfileSlice.reducer;
